import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import xss from "xss";

import console from "../utils/functions/console";
import AuftragStorage from "../storage/auftrag-storage";
import { RESPONSE_HEADER } from "../utils/enums";

export const fetchData = createAsyncThunk("auftrag/fetchData", ({ apiCall, query }, _trunkApi) => {
  if (typeof apiCall !== "function") return Promise.reject(new Error("invalid api call auftrag/fetchData"));  
  return new Promise((resolve, reject) => {
    try {
      apiCall(query, (error, data, response) => {
        if (response && data && !error) {
          if (data.header && data.header.status === RESPONSE_HEADER.STATUS.SUCCESSFUL) {
            resolve(data);
          } else if (data.header && data.header.errorNr) {
            reject(new Error("Fehler " + data.header.errorNr + ": Die Liste der Aufträge konnte nicht ermittelt werden"));
          } else {
            reject(new Error("Bei der Ermittlung der Aufträge ist ein Fehler aufgetreten"));
          }
        } else {
          console.error(error);
          reject(new Error("Die Auftragsliste konnten nicht geladen werden"));
        }
      });
    } catch (e) {
      console.error(e);
      reject(e);
    }
  });
});

export const fetchAuftrag = createAsyncThunk("auftrag/fetchAuftrag", ({ apiCall, auftragsNummer }, _trunkApi) => {
  if (typeof apiCall !== "function") return Promise.reject(new Error("invalid api call auftrag/fetchAuftrag"));
  return new Promise((resolve, reject) => {
    try {
      apiCall(auftragsNummer, (error, data, response) => {
        if (response && data && !error) {
          if (!data.header || data.header.status !== RESPONSE_HEADER.STATUS.SUCCESSFUL || !data.auftrag) {
            reject(new Error("Die Daten zum Auftrag konnten nicht geladen werden " + (data.header || {}).errorNr || ""));
          }
          resolve(data);
        } else {
          console.error(error);
          reject(new Error("Die Daten zum Auftrag konnten nicht geladen werden"));
        }
      });
    } catch (e) {
      console.error(e);
      reject(e);
    }
  });
});

export const getPdf = createAsyncThunk("auftrag/getPdf", ({ apiCall, auftragsNummer }, _trunkApi) => {
  if (typeof apiCall !== "function") return Promise.reject(new Error("invalid api call auftrag/getPdf"));
  return new Promise((resolve, reject) => {
    try {
      apiCall(auftragsNummer, (error, _data, response) => {
        if (error && error.rawResponse && String(error.rawResponse).substring(0, 4) === "%PDF") {
          const pdfBlob = new Blob([error.rawResponse], { type: "application/pdf" });
          const pdf = window.URL.createObjectURL(pdfBlob);
          resolve({ aktion: "reprint", auftragsNummer, pdf });
        }
        if (response && response.body && response.body.type === "application/pdf" && !error) {
          const pdf = window.URL.createObjectURL(response.body);
          resolve({ aktion: "reprint", auftragsNummer, pdf });
        } else {
          console.error(error);
          reject(new Error("Die PDF zum Auftrag konnten nicht geladen werden"));
        }
      });
    } catch (e) {
      console.error(e);
      reject(e);
    }
  });
});

export const setAction = createAsyncThunk("auftrag/setAction", ({ apiCall, auftragsNummer, aktion, stornoText, bearbeiter, fetch }, { dispatch }) => {
  if (typeof apiCall !== "function") return Promise.reject(new Error("invalid api call auftrag/setAction"));
  return new Promise((resolve, reject) => {
    try {
      apiCall({ auftragsNummer, stornoText, bearbeiter }, (error, data, response) => {
        if (response && data && !error) {
          if (data.header && data.header.status === RESPONSE_HEADER.STATUS.SUCCESSFUL) {
            if (fetch) dispatch(fetchAuftrag({ apiCall: fetch, auftragsNummer }));
            resolve({ aktion, auftragsNummer, orderId: data.orderId, message: data.message });
          } else if (data.header && data.header.status === RESPONSE_HEADER.STATUS.ERROR) {
            if (RESPONSE_HEADER.ERROR[data.header.errorNr]) {
              reject(new Error(RESPONSE_HEADER.ERROR[data.header.errorNr]));
            } else if (data.header.errorMsg) {
              reject(new Error(xss(data.header.errorMsg)));
            } else {
              reject(new Error(
                new Date().toLocaleString("de-DE") +
                (data.header.errorNr ? ` (${data.header.errorNr})` : "") +
                ": " +
                RESPONSE_HEADER.ERRORTITEL.UNBEKANNT
              ));
            }
          } else {
            reject(new Error("Es ist ein unbekannter Fehler aufgetreten"));
          }
        } else {
          if (error && error.rawResponse && String(error.rawResponse).substring(0, 4) === "%PDF") {
            const pdfBlob = new Blob([error.rawResponse], { type: "application/pdf" });
            const pdf = window.URL.createObjectURL(pdfBlob);
            resolve({ aktion, auftragsNummer, pdf });
          }
          console.error(error);
          reject(new Error("Die Aktion konnten nicht ausgeführt werden"));
        }
      });
    } catch (e) {
      console.error(e);
      reject(e);
    }
  });
});

const auftragSlice = createSlice({
  name: "auftrag",
  initialState: new AuftragStorage(),
  reducers: {
    getListe(state, action) {
      state.setListe(action.payload);
    },
    getAuftrag(state) {
      return state.getAuftrag();
    },
    getListeExtended(state, action) {
      state.setListeExtended(action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchData.pending, (state) => {
        state.failed(null);
        state.loading(true);
        return state;
      })
      .addCase(fetchData.fulfilled, (state, action) => {
        const res = action.payload;
        const query = action.meta.arg.query || {};
        state.setListe({ liste: res.auftragsliste, weiterKz: res.weiterKz, query });
        state.loading(false);
        return state;
      })
      .addCase(fetchData.rejected, (state, action) => {
        state.failed(action.error);
        state.setListe({});
        state.loading(false);
        return state;
      })      
      .addCase(fetchAuftrag.pending, (state) => {
        return state;
      })
      .addCase(fetchAuftrag.fulfilled, (state, action) => {
        let res = action.payload;
        state.setAuftrag(res.auftrag);
        return state;
      })
      .addCase(fetchAuftrag.rejected, (state, action) => {
        state.setAuftrag(null);
        state.failed(action.error);
        return state;
      })
      .addCase(getPdf.pending, (state, action) => {
        state.setActionStatus({ pending: true, auftragsNummer: action?.meta?.arg?.auftragsNummer, message: "Loading ...", variant: "info" });
        return state;
      })
      .addCase(getPdf.fulfilled, (state, action) => {
        state.setActionStatus({ ...action.payload });
        return state;
      })
      .addCase(getPdf.rejected, (state, action) => {
        state.setActionStatus({ auftragsNummer: action?.meta?.arg?.auftragsNummer, message: action?.error?.message, variant: "danger" });
        return state;
      })
      .addCase(setAction.pending, (state, action) => {
        state.setActionStatus({ pending: true, auftragsNummer: action?.meta?.arg?.auftragsNummer, message: "Loading ...", variant: "info" });
        return state;
      })
      .addCase(setAction.fulfilled, (state, action) => {
        state.setActionStatus({ ...action.payload });
        return state;
      })
      .addCase(setAction.rejected, (state, action) => {
        state.setActionStatus({ auftragsNummer: action?.meta?.arg?.auftragsNummer, message: action?.error?.message, variant: "danger" });
        return state;
      });
  }
});
export const { getListe, getAuftrag } = auftragSlice.actions;
export const auftragActions = auftragSlice.actions;
export default auftragSlice.reducer;
